import React, { Suspense, lazy, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAssistantDockHost } from '../../services/assistant-dock';
import { useQuoteBrainWriting } from '../../hooks/use-quote-brain-writing';
import { AssistantErrorBoundary } from '../AssistantErrorBoundary';
import { handleAssistantOpenProject } from '../../utils/assistant-app-bridge';
import { isSiteVisitRole } from '../../config/access-areas';

const QuoteMakerAssistantPanel = lazy(() => import('../../pages/stages/QuoteMakerAssistantPanel'));

export interface AppAssistantPanelWrapperProps {
    apiKey?: string | null;
    /**
     * The signed-in user's role. A site assessor (either spelling, see
     * isSiteVisitRole) gets no assistant at all: not mounted, not lazy-loaded.
     * `apiKey` alone is never falsy in production, so before 2026-09-22 this
     * wrapper mounted the panel for every role.
     */
    userRole?: string | null;
    currentPage: string;
    currentProjectId: string | null;
    assistantDraftSessionKey: string | null;
    assistantStageNavigationState: any;
    globalAiAssistantOpen: boolean;
    aiAssistantViewToggleSignal: number;
    setGlobalAiAssistantOpen: (open: boolean) => void;
    stage: number;
    quoteEditorOpen: boolean;
    quoteEditorIsEditing: boolean;
    showConfirm: any;
    model: string;
    formData: any;
    quoteTitle: string;
    setFormData: (updater: any) => void;
    analysis: any;
    diagnosticResult: any;
    scope: any[];
    setScope: (updater: any) => void;
    photos: any[];
    documents: any[];
    setDocuments: (updater: any) => void;
    videos: any[];
    quoteData: any;
    quoteLiveSnapshot: any;
    /** App's one quote objective (useQuoteObjective().text), for the Assistant's quote header (F14). */
    quoteObjectiveText?: string | null;
    setSettingsOpen: (open: boolean) => void;
    handleNavigate: (page: string) => void;
    handleQuoteStageNavigate: (targetStage: number) => any;
    toggleGlobalFileExplorer: () => void;
    isFileExplorerOpen: boolean;
    scopeSnapshotCallbackRef: React.MutableRefObject<((label: string, details: any) => void) | null>;
    setClientRefreshTrigger: React.Dispatch<React.SetStateAction<number>>;
    setGlobalAiProcessing: (processing: boolean) => void;
    quoteNote: string;
    setQuoteNote: (updater: any) => void;
    projectDefaultMargin: any;
    /** App's Stage 4 GST basis setter, the one the Scope Builder's inc/ex toggle calls. */
    setScopeGstInclusive?: ((isGstInclusive: boolean) => void) | null;
    /** App's HBCF notice: an Assistant change took the work total across the HBCF threshold. */
    onHbcfThresholdCrossed?: ((notice: { message: string; actionLabel: string; projectId?: unknown; change?: unknown }) => void) | null;
    currentBusinessPricingSnapshot: any;
    businessPricingSnapshotStatus: string;
    projectStrategy: any;
    projectWMS: any;
    quoteEditorActionsRef: any;
    /** The Stage 5 Jobs prop (buildQuoteJobEditorProp), null while Jobs is off. */
    quoteJob?: any;
    /** The job on screen is written by hand (Write it yourself), so QuoteMaker does not start on it. */
    quoteMakerByHand?: boolean;
    projects: any[];
    originalAiScope: any;
    scopeGstInclusive: boolean;
    isAnalyzing: boolean;
    isSummarizing: boolean;
    isGenerating: boolean;
    photoAssignBusy: boolean;
    projectWMSGenerating: boolean;
    lastAssignedPhotoIdsRef: React.MutableRefObject<Set<string> | null>;
    intakeAssistantContext: any;
    cancelAiPipeline: () => void;
    retryPhotoAssignment: () => void;
    /** Full AI's recorded doubt about the request source for this stage (use-rfq-source-doubt), or null. */
    rfqSourceDoubt?: any;
    onRfqSourceDoubtSwitch?: ((reading: any) => Promise<unknown> | unknown) | null;
    onRfqSourceDoubtKeep?: ((reading: any) => void) | null;
    onRfqSourceDoubtClose?: ((reading: any) => void) | null;
    quoteMakerReview?: any;
    onQuoteMakerReviewDone?: (() => void) | null;
    isCurrentProjectQuoteGenerationInFlight: () => boolean;
    loadProject: (idOrSummary: any) => Promise<any> | void;
    /** Decision D1: the persistence hook's Quote Run refusal (true = refused, message shown). */
    refuseProjectChangeDuringQuoteRun?: (change: 'open', projectId?: string) => boolean;
    /** A3 review: true when the project is the Quote Run's job in progress; it has been shown. */
    showQuoteRunJobInsteadOfOpening?: (projectId?: string) => boolean;
    /** The project load that last painted: an open moves to its stage only once it painted. */
    readPaintedProjectLoad?: () => { projectId?: unknown } | null | undefined;
    /** The page on screen now: an open moves to its stage only once QuoteMaker shows it. */
    readLivePage?: () => unknown;
    /** App's stage navigation from its latest render; it never leaves the page on screen. */
    navigateToQuoteStageLive?: (stage: number) => any;
}

export const AppAssistantPanelWrapper: React.FC<AppAssistantPanelWrapperProps> = ({
    apiKey,
    userRole,
    currentPage,
    currentProjectId,
    assistantDraftSessionKey,
    assistantStageNavigationState,
    globalAiAssistantOpen,
    aiAssistantViewToggleSignal,
    setGlobalAiAssistantOpen,
    stage,
    quoteEditorOpen,
    quoteEditorIsEditing,
    showConfirm,
    model,
    formData,
    quoteTitle,
    setFormData,
    analysis,
    diagnosticResult,
    scope,
    setScope,
    photos,
    documents,
    setDocuments,
    videos,
    quoteData,
    quoteLiveSnapshot,
    quoteObjectiveText = null,
    setSettingsOpen,
    handleNavigate,
    handleQuoteStageNavigate,
    toggleGlobalFileExplorer,
    isFileExplorerOpen,
    scopeSnapshotCallbackRef,
    setClientRefreshTrigger,
    setGlobalAiProcessing,
    quoteNote,
    setQuoteNote,
    projectDefaultMargin,
    setScopeGstInclusive = null,
    onHbcfThresholdCrossed = null,
    currentBusinessPricingSnapshot,
    businessPricingSnapshotStatus,
    projectStrategy,
    projectWMS,
    quoteEditorActionsRef,
    quoteJob = null,
    quoteMakerByHand = false,
    projects,
    originalAiScope,
    scopeGstInclusive,
    isAnalyzing,
    isSummarizing,
    isGenerating,
    photoAssignBusy,
    projectWMSGenerating,
    lastAssignedPhotoIdsRef,
    intakeAssistantContext,
    cancelAiPipeline,
    retryPhotoAssignment,
    rfqSourceDoubt = null,
    onRfqSourceDoubtSwitch = null,
    onRfqSourceDoubtKeep = null,
    onRfqSourceDoubtClose = null,
    quoteMakerReview = null,
    onQuoteMakerReviewDone = null,
    isCurrentProjectQuoteGenerationInFlight,
    loadProject,
    refuseProjectChangeDuringQuoteRun,
    showQuoteRunJobInsteadOfOpening,
    readPaintedProjectLoad,
    readLivePage,
    navigateToQuoteStageLive,
}) => {
    // One Assistant for the whole app, shown floating or docked in a stage
    // cockpit's Assistant tab (assistant-dock.ts). It always renders into the
    // same slot element, and the slot itself is moved between this wrapper's
    // place in the page and the cockpit tab. React never sees a different
    // container, so a conversation, a typed prompt and a running turn all
    // survive the move. (A portal whose container changes is remounted.)
    const cockpitHost = useAssistantDockHost();
    // While QuoteMaker writes the job open at Stage 4, Stage 4 is inert (AppStageViewsContainer, O1), and so would be
    // an Assistant docked in its cockpit: no status question, no Stop from the chat. It floats over the page until the
    // run ends, then docks again. The Stage 5 editor's own cockpit is never locked.
    const quoteMakerWriting = useQuoteBrainWriting(currentProjectId);
    const dockHost = quoteMakerWriting && !quoteEditorOpen && stage === 4 ? null : cockpitHost;
    // Floating only because of the lock: it stays open as it was in the cockpit, and its own close button closes it.
    const floatingForLock = !!cockpitHost && !dockHost;
    const [closedDuringLock, setClosedDuringLock] = useState(false);
    useEffect(() => { if (!floatingForLock) setClosedDuringLock(false); }, [floatingForLock]);
    const [slot] = useState<HTMLDivElement | null>(() => {
        if (typeof document === 'undefined') return null;
        const el = document.createElement('div');
        el.className = 'qm-assistant-dock-slot';
        return el;
    });
    const homeRef = useRef<HTMLDivElement | null>(null);
    useLayoutEffect(() => {
        if (!slot) return;
        const target = dockHost || homeRef.current;
        if (!target) return;
        // Floating, the slot must not box the panel's fixed layers.
        slot.style.display = dockHost ? '' : 'contents';
        if (slot.parentNode !== target) target.appendChild(slot);
    });
    useEffect(() => () => { slot?.remove(); }, [slot]);
    // Docked, the cockpit tab is what shows the Assistant: leaving the tab
    // hides it rather than popping the floating panel over the page.
    useEffect(() => {
        if (dockHost && globalAiAssistantOpen) setGlobalAiAssistantOpen(false);
    }, [dockHost, globalAiAssistantOpen, setGlobalAiAssistantOpen]);

    if (!apiKey) return null;
    // Role gate: the assistant is a quoting tool. A site assessor captures a
    // property and confirms a visit; nothing they do needs it, and the field
    // report of 2026-09-22 (Android Brave, 4G) was of a phone surface with too
    // many things that could fail. Returning null here means the chunk is never
    // fetched for them either.
    if (isSiteVisitRole(userRole)) return null;

    const panel = (
        <Suspense fallback={null}>
            <AssistantErrorBoundary>
                <QuoteMakerAssistantPanel
                    dockHost={dockHost}
                    currentPage={currentPage}
                    currentProjectId={currentProjectId}
                    assistantDraftSessionKey={assistantDraftSessionKey}
                    stageNavigationState={assistantStageNavigationState}
                    isOpen={globalAiAssistantOpen || !!dockHost || (floatingForLock && !closedDuringLock)}
                    viewToggleSignal={aiAssistantViewToggleSignal}
                    onClose={() => {
                        if (floatingForLock) setClosedDuringLock(true);
                        setGlobalAiAssistantOpen(false);
                    }}
                    stage={quoteEditorOpen ? 5 : stage}
                    quoteEditorOpen={quoteEditorOpen}
                    quoteEditorIsEditing={quoteEditorIsEditing}
                    apiKey={apiKey}
                    showConfirm={showConfirm}
                    model={model}
                    formData={formData}
                    projectTitle={quoteTitle}
                    setFormData={(updater: any) => {
                        if (isCurrentProjectQuoteGenerationInFlight()) return;
                        setFormData(updater);
                    }}
                    analysis={analysis}
                    diagnosticResult={diagnosticResult}
                    scope={scope}
                    setScope={(updater: any) => {
                        if (isCurrentProjectQuoteGenerationInFlight()) return;
                        setScope(updater);
                    }}
                    isScopeWriteBlocked={isCurrentProjectQuoteGenerationInFlight}
                    photos={photos}
                    documents={documents}
                    setDocuments={(updater: any) => {
                        if (isCurrentProjectQuoteGenerationInFlight()) return;
                        setDocuments(updater);
                    }}
                    videos={videos}
                    quoteData={quoteData}
                    quoteLiveSnapshot={quoteLiveSnapshot}
                    quoteObjectiveText={quoteObjectiveText}
                    onSettingsClick={() => setSettingsOpen(true)}
                    onNavigate={handleNavigate}
                    onStageNavigate={handleQuoteStageNavigate}
                    onOpenFileExplorer={toggleGlobalFileExplorer}
                    isFileExplorerOpen={isFileExplorerOpen}
                    // QuoteMakerAssistantPanel declares this prop as `() => void`
                    // but always calls it with (label, details); see its onBeforeScopeChange call.
                    onBeforeScopeChange={(label?: string, details?: any) => {
                        if (scopeSnapshotCallbackRef?.current) {
                            scopeSnapshotCallbackRef.current(label as string, details);
                        }
                    }}
                    onClientAction={(action: any) => {
                        if (action?.type === 'refresh') setClientRefreshTrigger((t: number) => t + 1);
                    }}
                    onProcessingChange={setGlobalAiProcessing}
                    quoteNote={quoteNote}
                    setQuoteNote={(updater: any) => {
                        if (isCurrentProjectQuoteGenerationInFlight()) return;
                        setQuoteNote(updater);
                    }}
                    projectDefaultMargin={projectDefaultMargin}
                    // A GST basis change while a quote is generated for this
                    // project would race the generation, like a scope write;
                    // the panel keeps such a proposal pending.
                    onHbcfThresholdCrossed={onHbcfThresholdCrossed}
                    onStage4GstModeChange={typeof setScopeGstInclusive === 'function'
                        ? (isGstInclusive: boolean) => {
                            if (isCurrentProjectQuoteGenerationInFlight()) return;
                            setScopeGstInclusive(isGstInclusive);
                        }
                        : null}
                    businessPricingSnapshot={currentBusinessPricingSnapshot}
                    businessPricingSnapshotStatus={businessPricingSnapshotStatus}
                    projectStrategy={projectStrategy}
                    projectWMS={projectWMS}
                    quoteActionsRef={quoteEditorActionsRef}
                    quoteJob={quoteJob}
                    quoteMakerByHand={quoteMakerByHand}
                    projectSummary={(currentProjectId && projects.find((p: any) => p.id === currentProjectId)) || null}
                    aiContext={(projects.find((p: any) => p.id === currentProjectId)?.aiContext) || null}
                    originalAiScope={originalAiScope}
                    scopeGstInclusive={scopeGstInclusive}
                    aiPipelineStatus={{
                        isAnalyzing,
                        isSummarizing,
                        isGenerating,
                        photoAssignBusy,
                        projectWMSGenerating,
                        lastAssignedPhotoCount: lastAssignedPhotoIdsRef?.current?.size || 0,
                    }}
                    intakeAssistantContext={currentPage === 'intake' ? intakeAssistantContext : null}
                    cancelAiPipeline={cancelAiPipeline}
                    retryPhotoAssignment={retryPhotoAssignment}
                    rfqSourceDoubt={rfqSourceDoubt}
                    onRfqSourceDoubtSwitch={onRfqSourceDoubtSwitch}
                    onRfqSourceDoubtKeep={onRfqSourceDoubtKeep}
                    onRfqSourceDoubtClose={onRfqSourceDoubtClose}
                    quoteMakerReview={quoteMakerReview}
                    onQuoteMakerReviewDone={onQuoteMakerReviewDone}
                    onOpenProjectFromAssistant={(params: any) => handleAssistantOpenProject({
                        params,
                        projects,
                        handleNavigate,
                        loadProject,
                        handleQuoteStageNavigate,
                        refuseProjectChangeDuringQuoteRun,
                        showQuoteRunJobInsteadOfOpening,
                        readPaintedProjectLoad,
                        readLivePage,
                        navigateToQuoteStageLive,
                    })}
                />
            </AssistantErrorBoundary>
        </Suspense>
    );

    return (
        <>
            <div ref={homeRef} data-qm-assistant-home="" style={{ display: 'contents' }} />
            {slot ? createPortal(panel, slot) : panel}
        </>
    );
};

export default AppAssistantPanelWrapper;
